import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';

import { meterReadingsImportApi } from '../api/meterReadingsImport';
import { residentialComplexesApi } from '../api/residentialComplexes';
import ConfirmDialog from '../components/ConfirmModal';
import { $canEditComplex, $tenantDataStartDate } from '../store/auth';

const DATE_INPUT_STYLE = { minWidth: 180 };
const SELECT_MENU_PROPS = {
  PaperProps: {
    elevation: 0,
    sx: {
      mt: 0.75,
      borderRadius: 2.5,
      border: '1px solid rgba(148, 163, 184, 0.28)',
      boxShadow: '0 20px 44px rgba(15, 23, 42, 0.14)',
      overflow: 'hidden',
      '& .MuiList-root': {
        py: 0.75,
        backgroundColor: '#FFFFFF',
      },
      '& .MuiMenuItem-root': {
        minHeight: 42,
        margin: '2px 8px',
        borderRadius: 2,
        fontSize: 14,
        fontWeight: 600,
        color: '#0F172A',
        transition: 'background-color 140ms ease, color 140ms ease',
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(37, 99, 235, 0.12)',
        color: '#1D4ED8',
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: 'rgba(37, 99, 235, 0.18)',
      },
    },
  },
};
const SELECT_FIELD_SX = {
  minWidth: 220,
  '& .MuiInputLabel-root': {
    fontWeight: 600,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
    transition: 'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
    '& fieldset': {
      borderColor: 'rgba(148, 163, 184, 0.34)',
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: '#FCFDFE',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(37, 99, 235, 0.34)',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.08)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2563EB',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    fontWeight: 600,
  },
  '& .MuiNativeSelect-icon': {
    color: '#475569',
    right: 12,
  },
};
const IMPORT_MODE_HINTS = {
  current:
    'В файле указано последнее значение прибора на дату выгрузки. История до этой даты берётся из уже сохранённых показаний.',
  history:
    'В файле есть несколько показаний по одному прибору. Система сохранит их как историю и использует нужные значения за период.',
};
const HOVER_ACTION_BUTTON_SX = {
  width: 38,
  height: 38,
  borderRadius: 2.5,
  border: '1px solid rgba(148,163,184,0.18)',
  background: 'rgba(255,255,255,0.96)',
  color: '#475569',
  boxShadow: '0 10px 22px rgba(15,23,42,0.08)',
  transition: 'all 160ms ease',
  '&:hover': {
    background: '#ffffff',
    color: '#0f172a',
    transform: 'translateY(-1px)',
    boxShadow: '0 14px 26px rgba(15,23,42,0.12)',
  },
};

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Дата' },
  { value: 'file_name', label: 'Файл' },
  { value: 'total_rows', label: 'Строки' },
  { value: 'imported_rows', label: 'Успешно' },
  { value: 'skipped_rows', label: 'Пропущено' },
];

const SORT_DIR_OPTIONS = [
  { value: 'desc', label: 'Сначала новые' },
  { value: 'asc', label: 'Сначала старые' },
];

function detectFileKind(fileName) {
  const lower = String(fileName || '').toLowerCase();
  if (lower.endsWith('.xml')) {
    return {
      label: 'XML',
      color: 'info',
      icon: <DataObjectRoundedIcon sx={{ fontSize: 14 }} />,
      isIsta: true,
    };
  }
  if (lower.endsWith('.xlsx')) {
    return {
      label: 'XLSX',
      color: 'success',
      icon: <GridViewRoundedIcon sx={{ fontSize: 14 }} />,
      isIsta: false,
    };
  }
  if (lower.endsWith('.csv')) {
    return {
      label: 'CSV',
      color: 'default',
      icon: <TableRowsRoundedIcon sx={{ fontSize: 14 }} />,
      isIsta: false,
    };
  }
  if (lower.endsWith('.zip')) {
    return {
      label: 'ZIP',
      color: 'warning',
      icon: <UploadFileRoundedIcon sx={{ fontSize: 14 }} />,
      isIsta: false,
    };
  }
  return {
    label: 'Файл',
    color: 'default',
    icon: null,
    isIsta: false,
  };
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return '0 Б';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / 1024 / 1024).toFixed(1)} МБ`;
}

function getImportIssueAction(error) {
  const message = String(error?.message || '').toLowerCase();
  if (message.includes('не найден')) {
    return 'Добавьте устройство или оформите замену';
  }
  if (message.includes('другим значением')) {
    return 'Проверьте существующее показание';
  }
  if (message.includes('не относится')) {
    return 'Проверьте выбранный дом';
  }
  return 'Проверьте данные строки';
}

function MetricCard({ label, value, tone = 'default' }) {
  const tones = {
    default: {
      bg: 'rgba(248, 250, 252, 0.88)',
      border: 'rgba(148, 163, 184, 0.2)',
      value: 'text.primary',
    },
    info: {
      bg: 'rgba(240, 249, 255, 0.92)',
      border: 'rgba(14, 165, 233, 0.2)',
      value: '#0369A1',
    },
    success: {
      bg: 'rgba(240, 253, 244, 0.92)',
      border: 'rgba(34, 197, 94, 0.18)',
      value: '#15803D',
    },
    warning: {
      bg: 'rgba(255, 251, 235, 0.92)',
      border: 'rgba(245, 158, 11, 0.22)',
      value: '#B45309',
    },
  };

  const palette = tones[tone] || tones.default;

  return (
    <Box
      sx={{
        minWidth: 132,
        px: 1.5,
        py: 1.35,
        borderRadius: 2.5,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.45 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: palette.value, lineHeight: 1 }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function StageCard({ index, title, description, active = false, completed = false, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: 2,
        borderColor: active
          ? 'rgba(14, 165, 233, 0.3)'
          : completed
            ? 'rgba(34, 197, 94, 0.22)'
            : 'rgba(148, 163, 184, 0.18)',
        background: active
          ? 'linear-gradient(180deg, rgba(248,252,255,0.96) 0%, rgba(255,255,255,0.98) 100%)'
          : 'rgba(255,255,255,0.98)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: completed ? '#166534' : active ? '#0369A1' : 'text.secondary',
              background: completed
                ? 'rgba(220, 252, 231, 0.92)'
                : active
                  ? 'rgba(224, 242, 254, 0.92)'
                  : 'rgba(241, 245, 249, 0.92)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
            }}
          >
            {index}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

export default function MeterReadingsImportHistoryPage() {
  const navigate = useNavigate();
  const [canRollback, tenantDataStartDate] = useUnit([$canEditComplex, $tenantDataStartDate]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem('sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });

  const [complexes, setComplexes] = useState([]);
  const [complexesLoading, setComplexesLoading] = useState(false);
  const [selectedComplexId, setSelectedComplexId] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [importMode, setImportMode] = useState('history');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadItems, setUploadItems] = useState([]);
  const uploadFile = uploadFiles[0] || null;

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBatch, setConfirmBatch] = useState(null);
  const [rollingBackId, setRollingBackId] = useState(null);
  const [issuesDialogOpen, setIssuesDialogOpen] = useState(false);

  const loadComplexes = useCallback(async () => {
    try {
      setComplexesLoading(true);
      const data = await residentialComplexesApi.getResidentialComplexes(0, 100);
      setComplexes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('load complexes failed', err);
      setComplexes([]);
      setError((current) => current || 'Не удалось загрузить список объектов.');
    } finally {
      setComplexesLoading(false);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * pageSize;
      const data = await meterReadingsImportApi.listBatches({
        limit: pageSize,
        offset,
        q: query.trim() || undefined,
        date_from: fromDate ? `${fromDate}T00:00:00` : undefined,
        date_to: toDate ? `${toDate}T23:59:59` : undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setBatches(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error('load batches failed', err);
      setError('Не удалось загрузить историю импортов.');
    } finally {
      setLoading(false);
    }
  }, [page, query, fromDate, toDate, sortBy, sortDir]);

  useEffect(() => {
    loadComplexes();
  }, [loadComplexes]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    const syncSidebarState = (event) => {
      if (typeof event?.detail?.collapsed === 'boolean') {
        setSidebarCollapsed(event.detail.collapsed);
        return;
      }
      try {
        setSidebarCollapsed(window.localStorage.getItem('sidebar-collapsed') === '1');
      } catch {
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('sidebar:toggle', syncSidebarState);
    window.addEventListener('storage', syncSidebarState);
    return () => {
      window.removeEventListener('sidebar:toggle', syncSidebarState);
      window.removeEventListener('storage', syncSidebarState);
    };
  }, []);

  const selectedComplex = useMemo(
    () => complexes.find((item) => String(item.id) === String(selectedComplexId)) || null,
    [complexes, selectedComplexId]
  );

  const buildingOptions = useMemo(
    () => (Array.isArray(selectedComplex?.buildings) ? selectedComplex.buildings : []),
    [selectedComplex]
  );

  const selectedBuilding = useMemo(
    () => buildingOptions.find((item) => String(item.id) === String(selectedBuildingId)) || null,
    [buildingOptions, selectedBuildingId]
  );

  useEffect(() => {
    if (selectedComplexId || complexes.length !== 1) return;
    setSelectedComplexId(String(complexes[0].id));
  }, [complexes, selectedComplexId]);

  useEffect(() => {
    if (!selectedComplexId) {
      setSelectedBuildingId('');
      return;
    }
    if (selectedBuildingId && buildingOptions.some((item) => String(item.id) === String(selectedBuildingId))) {
      return;
    }
    if (buildingOptions.length === 1) {
      setSelectedBuildingId(String(buildingOptions[0].id));
      return;
    }
    setSelectedBuildingId('');
  }, [buildingOptions, selectedBuildingId, selectedComplexId]);

  const addUploadFiles = useCallback((fileList) => {
    const nextFiles = Array.from(fileList || []).filter(Boolean);
    if (!nextFiles.length) return;

    setUploadFiles((currentFiles) => {
      const seen = new Set(
        currentFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`)
      );
      const uniqueNextFiles = nextFiles.filter((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [...currentFiles, ...uniqueNextFiles];
    });
    setUploadItems([]);
    setUploadResult(null);
    setUploadStatus('');
    setError('');
  }, []);

  const handleFilesDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      addUploadFiles(event.dataTransfer?.files);
    },
    [addUploadFiles]
  );

  const formatDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('ru-RU');
  };

  const requestRollback = (batch) => {
    if (!canRollback) return;
    setConfirmBatch(batch);
    setConfirmOpen(true);
  };

  const handleRollback = async () => {
    if (!confirmBatch?.id || rollingBackId) return;
    try {
      setRollingBackId(confirmBatch.id);
      await meterReadingsImportApi.rollbackBatch(confirmBatch.id);
      await loadBatches();
    } catch (err) {
      console.error('rollback failed', err);
      setError('Не удалось откатить импорт. Попробуйте позже.');
    } finally {
      setRollingBackId(null);
      setConfirmOpen(false);
      setConfirmBatch(null);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await meterReadingsImportApi.exportBatches({
        q: query.trim() || undefined,
        date_from: fromDate ? `${fromDate}T00:00:00` : undefined,
        date_to: toDate ? `${toDate}T23:59:59` : undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'meter_readings_imports.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export failed', err);
      setError('Не удалось выгрузить CSV.');
    }
  };

  const handleUpload = async () => {
    if (!selectedComplexId) {
      setError('Выберите жилой комплекс.');
      return;
    }
    if (!selectedBuildingId) {
      setError('Выберите дом.');
      return;
    }
    if (!uploadFile) {
      setError('Выберите файл для загрузки.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const result = await meterReadingsImportApi.uploadFile(uploadFile, {
        import_mode: importMode,
        building_id: selectedBuildingId,
      });
      setUploadResult(result);
      await loadBatches();
    } catch (err) {
      console.error('upload failed', err);
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить файл.');
    } finally {
      setUploading(false);
    }
  };
  void handleUpload;

  const handleUploadMany = async () => {
    setUploadStatus('Проверяем данные...');
    if (!selectedComplexId) {
      setError('Выберите жилой комплекс.');
      setUploadStatus('');
      return;
    }
    if (!selectedBuildingId) {
      setError('Выберите дом.');
      setUploadStatus('');
      return;
    }
    if (!uploadFiles.length) {
      setError('Выберите файлы для загрузки.');
      setUploadStatus('');
      return;
    }

    setUploading(true);
    setError('');
    setUploadItems([]);
    setUploadStatus(`Загружаем файлы: 0 из ${uploadFiles.length}`);

    try {
      const nextItems = [];
      const runtimeCrypto = typeof window !== 'undefined' ? window.crypto : null;
      const uploadGroupId =
        uploadFiles.length > 1
          ? (runtimeCrypto?.randomUUID?.() || `upload-group-${Date.now()}`)
          : undefined;

      for (const file of uploadFiles) {
        setUploadStatus(`Загружаем файл ${nextItems.length + 1} из ${uploadFiles.length}: ${file.name}`);
        try {
          const result = await meterReadingsImportApi.uploadFile(file, {
            import_mode: importMode,
            upload_group_id: uploadGroupId,
            building_id: selectedBuildingId,
          });
          nextItems.push({
            fileName: file.name,
            status: 'success',
            result,
          });
        } catch (fileError) {
          console.error('upload failed', fileError);
          nextItems.push({
            fileName: file.name,
            status: 'error',
            error:
              fileError?.response?.data?.detail ||
              fileError?.message ||
              'Не удалось загрузить файл.',
          });
        }
        setUploadItems([...nextItems]);
      }

      const successfulItems = nextItems.filter((item) => item.status === 'success');
      const failedItems = nextItems.filter((item) => item.status === 'error');
      const detectedFormats = Array.from(
        new Set(successfulItems.map((item) => item.result?.detected_format).filter(Boolean))
      );

      setUploadResult({
        total_rows: successfulItems.reduce((sum, item) => sum + (item.result?.total_rows || 0), 0),
        imported_rows: successfulItems.reduce((sum, item) => sum + (item.result?.imported_rows || 0), 0),
        skipped_rows: successfulItems.reduce((sum, item) => sum + (item.result?.skipped_rows || 0), 0),
        batch_id: successfulItems.length ? successfulItems[successfulItems.length - 1].result?.batch_id || null : null,
        detected_format:
          detectedFormats.length === 1 ? detectedFormats[0] : detectedFormats.length > 1 ? 'mixed' : null,
        import_mode: importMode,
        warnings: [
          ...successfulItems.flatMap((item) =>
            (item.result?.warnings || []).map((warning) => `${item.fileName}: ${warning}`)
          ),
          ...failedItems.map((item) => `${item.fileName}: ${item.error}`),
        ],
        errors: successfulItems.flatMap((item) =>
          (item.result?.errors || []).map((errorItem) => ({
            ...errorItem,
            file_name: errorItem.file_name || item.fileName,
          }))
        ),
      });

      if (failedItems.length) {
        setError(
          failedItems.length === uploadFiles.length
            ? 'Ни один файл не был загружен.'
            : `Часть файлов не загрузилась: ${failedItems.length} из ${uploadFiles.length}.`
        );
      }

      await loadBatches();
      setUploadStatus(
        failedItems.length
          ? `Импорт завершён с ошибками: ${successfulItems.length} из ${uploadFiles.length}`
          : `Импорт завершён: ${successfulItems.length} из ${uploadFiles.length}`
      );
    } catch (err) {
      console.error('upload many failed', err);
      setError(err?.response?.data?.detail || err?.message || 'Не удалось выполнить импорт.');
      setUploadStatus('');
    } finally {
      setUploading(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const xmlCountOnPage = useMemo(
    () => batches.filter((item) => String(item.file_name || '').toLowerCase().endsWith('.xml')).length,
    [batches]
  );
  const isXmlFile = !!uploadFile && String(uploadFile.name).toLowerCase().endsWith('.xml');
  const xmlFilesCount = uploadFiles.filter((file) => String(file.name).toLowerCase().endsWith('.xml')).length;
  const uniqueUploadKinds = Array.from(new Set(uploadFiles.map((file) => detectFileKind(file.name).label)));
  const uploadFilesSummary = useMemo(() => {
    const formatCounts = uploadFiles.reduce((acc, file) => {
      const format = detectFileKind(file.name).label;
      acc[format] = (acc[format] || 0) + 1;
      return acc;
    }, {});
    const totalSize = uploadFiles.reduce((sum, file) => sum + (file.size || 0), 0);

    return {
      totalSize,
      formatCounts,
      formatText: Object.entries(formatCounts)
        .map(([format, count]) => `${format}: ${count}`)
        .join(', '),
    };
  }, [uploadFiles]);
  const totalImports = total || batches.length;
  const importedCount = uploadResult?.imported_rows ?? '—';
  const skippedCount = uploadResult?.skipped_rows ?? '—';
  const warningCount = uploadResult?.errors?.length ?? 0;
  const importIssues = useMemo(() => uploadResult?.errors || [], [uploadResult?.errors]);
  const groupedBatches = useMemo(() => {
    const groups = [];
    const indexByKey = new Map();

    batches.forEach((batch) => {
      const key = batch.group_id || batch.id;
      const existingIndex = indexByKey.get(key);
      if (existingIndex == null) {
        indexByKey.set(key, groups.length);
        groups.push({ key, items: [batch] });
      } else {
        groups[existingIndex].items.push(batch);
      }
    });

    return groups.map((group) => {
      const items = [...group.items].sort((a, b) => {
        const left = new Date(a.created_at || 0).getTime();
        const right = new Date(b.created_at || 0).getTime();
        return right - left;
      });

      return {
        ...group,
        items,
        isMulti: items.length > 1,
        totalRows: items.reduce((sum, item) => sum + (item.total_rows || 0), 0),
        importedRows: items.reduce((sum, item) => sum + (item.imported_rows || 0), 0),
        skippedRows: items.reduce((sum, item) => sum + (item.skipped_rows || 0), 0),
        createdAt: items[0]?.created_at || null,
        author: items[0]?.created_by_email || items[0]?.created_by_user_id || null,
        hasXml: items.some((item) => detectFileKind(item.file_name).isIsta),
        rolledBack: items.every((item) => Boolean(item.rolled_back_at)),
      };
    });
  }, [batches]);
  const showActionsColumn = sidebarCollapsed;

  return (
    <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2 }, width: '100%' }}>
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.99)',
          }}
        >
          <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2.25, md: 2.75 } }}>
            <Stack spacing={2.25}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', lg: 'center' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 0.75 }}>
                    Центр импорта показаний
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={() => {
                      loadComplexes();
                      loadBatches();
                    }}
                    disabled={loading || complexesLoading || uploading}
                    variant="outlined"
                  >
                    Обновить
                  </Button>
                  <Button
                    startIcon={<FileDownloadRoundedIcon />}
                    onClick={handleExport}
                    disabled={loading}
                    variant="contained"
                  >
                    Экспорт CSV
                  </Button>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
                <MetricCard label="Всего импортов" value={totalImports} />
                <MetricCard label="XML на странице" value={xmlCountOnPage} tone="info" />
                <MetricCard label="Успешно в последней загрузке" value={importedCount} tone="success" />
                <MetricCard label="Пропущено" value={skippedCount} tone="warning" />
              </Stack>
            </Stack>
          </Box>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.3fr) minmax(360px, 0.7fr)' },
            alignItems: 'start',
          }}
        >
          <Stack spacing={2}>
            <StageCard
              index="1"
              title="Выберите объект"
              description="ЖК и дом"
              active={!selectedBuildingId}
              completed={Boolean(selectedBuildingId)}
            >
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <TextField
                  select
                  label="Жилой комплекс"
                  value={selectedComplexId}
                  onChange={(event) => {
                    setSelectedComplexId(event.target.value);
                    setSelectedBuildingId('');
                    setUploadResult(null);
                  }}
                  size="small"
                  sx={{ ...SELECT_FIELD_SX, minWidth: 280, flex: 1 }}
                  SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  disabled={complexesLoading || uploading}
                >
                  <MenuItem value="">Выберите комплекс</MenuItem>
                  {complexes.map((complex) => (
                    <MenuItem key={complex.id} value={complex.id}>
                      {complex.name || complex.address || complex.id}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Дом"
                  value={selectedBuildingId}
                  onChange={(event) => {
                    setSelectedBuildingId(event.target.value);
                    setUploadResult(null);
                  }}
                  size="small"
                  sx={{ ...SELECT_FIELD_SX, minWidth: 260, flex: 1 }}
                  SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
                  disabled={!selectedComplexId || uploading}
                >
                  <MenuItem value="">Выберите дом</MenuItem>
                  {buildingOptions.map((building) => (
                    <MenuItem key={building.id} value={building.id}>
                      {building.name || building.address || building.id}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="outlined"
                  startIcon={<OpenInNewRoundedIcon />}
                  disabled={!selectedBuildingId}
                  onClick={() => navigate(`/building/${selectedBuildingId}/corps`)}
                >
                  Открыть дом
                </Button>
              </Stack>

              {(selectedComplex || selectedBuilding) && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedComplex && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<ApartmentRoundedIcon sx={{ fontSize: 14 }} />}
                      label={selectedComplex.name || 'Комплекс'}
                    />
                  )}
                  {selectedBuilding && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<HomeWorkRoundedIcon sx={{ fontSize: 14 }} />}
                      label={selectedBuilding.name || selectedBuilding.address || 'Дом'}
                    />
                  )}
                </Stack>
              )}
            </StageCard>

            <StageCard
              index="2"
              title="Выберите сценарий"
              description="Тип загрузки"
              active={Boolean(selectedBuildingId) && !uploadResult}
              completed={Boolean(uploadResult)}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={importMode}
                onChange={(_, value) => {
                  if (!value) return;
                  setImportMode(value);
                  setUploadResult(null);
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1,
                  '& .MuiToggleButton-root': {
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    borderRadius: '16px !important',
                    border: '1px solid rgba(15, 23, 42, 0.14)',
                    px: 1.5,
                    py: 1.35,
                  },
                }}
              >
                <ToggleButton value="current">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Текущие показания
                    </Typography>
                    <Tooltip title={IMPORT_MODE_HINTS.current} arrow placement="top">
                      <InfoOutlinedIcon
                        sx={{ fontSize: 17, color: importMode === 'current' ? '#1D4ED8' : '#64748B' }}
                      />
                    </Tooltip>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="history">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Показания с историей
                    </Typography>
                    <Tooltip title={IMPORT_MODE_HINTS.history} arrow placement="top">
                      <InfoOutlinedIcon
                        sx={{ fontSize: 17, color: importMode === 'history' ? '#1D4ED8' : '#64748B' }}
                      />
                    </Tooltip>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </StageCard>

            <StageCard
              index="3"
              title="Загрузите файл"
              description="Файл"
              active={Boolean(selectedBuildingId)}
              completed={Boolean(uploadResult)}
            >
              <Stack spacing={1.5}>
                <Paper
                  variant="outlined"
                  onDragEnter={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragActive(false);
                  }}
                  onDrop={handleFilesDrop}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    borderStyle: 'dashed',
                    borderColor: dragActive ? '#2563EB' : 'rgba(148, 163, 184, 0.36)',
                    backgroundColor: dragActive ? 'rgba(37, 99, 235, 0.07)' : 'rgba(248, 250, 252, 0.74)',
                    transition: 'border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
                    boxShadow: dragActive ? '0 0 0 4px rgba(37, 99, 235, 0.08)' : 'none',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 220 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2.5,
                          display: 'grid',
                          placeItems: 'center',
                          color: '#2563EB',
                          backgroundColor: '#EFF6FF',
                        }}
                      >
                        <UploadFileRoundedIcon />
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0F172A' }}>
                          {uploadFiles.length ? `Файлов: ${uploadFiles.length}` : 'Перетащите файлы сюда'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          XML, CSV, XLSX, ZIP
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {uploadFile ? `Первый файл: ${uploadFile.name}` : 'Файлы пока не выбраны'}
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        {isXmlFile && (
                          <>
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              icon={<DataObjectRoundedIcon sx={{ fontSize: 14 }} />}
                              label="XML"
                            />
                            <Chip size="small" color="info" variant="outlined" label="ISTA" />
                          </>
                        )}
                        {!isXmlFile && uploadFile && (
                          <Chip size="small" variant="outlined" label={detectFileKind(uploadFile.name).label} />
                        )}
                        {xmlFilesCount > 1 && (
                          <Chip size="small" color="info" variant="outlined" label={`XML: ${xmlFilesCount}`} />
                        )}
                        {uniqueUploadKinds
                          .filter((label) => label !== 'XML')
                          .map((label) => (
                            <Chip key={label} size="small" variant="outlined" label={label} />
                          ))}
                        {uploadFiles.slice(0, 4).map((file) => (
                          <Chip
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            size="small"
                            variant="outlined"
                            label={file.name}
                          />
                        ))}
                        {uploadFiles.length > 4 && (
                          <Chip size="small" variant="outlined" label={`+${uploadFiles.length - 4}`} />
                        )}
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                      {uploadFiles.length > 0 && (
                        <Button
                          variant="text"
                          disabled={uploading}
                          onClick={() => {
                            setUploadFiles([]);
                            setUploadItems([]);
                            setUploadResult(null);
                            setUploadStatus('');
                            setError('');
                          }}
                        >
                          Очистить
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<UploadFileRoundedIcon />}
                        component="label"
                        disabled={uploading}
                      >
                        Выбрать
                        <input
                          hidden
                          type="file"
                          multiple
                          accept=".csv,.xlsx,.xml,.zip"
                          onChange={(event) => {
                            addUploadFiles(event.target.files);
                            event.target.value = '';
                          }}
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleUploadMany();
                        }}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />}
                      >
                        {uploading ? 'Загружаем...' : 'Запустить импорт'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                {(uploadStatus || error) && (
                  <Alert severity={error ? 'error' : uploadStatus.includes('ошиб') ? 'warning' : 'info'}>
                    {error || uploadStatus}
                  </Alert>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: 2.5,
                    borderColor: 'rgba(148, 163, 184, 0.22)',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Режим: ${importMode === 'history' ? 'Показания с историей' : 'Текущие показания'}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Файлы: ${uploadFiles.length || 0}`}
                      color={uploadFiles.length ? 'primary' : 'default'}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Размер: ${formatFileSize(uploadFilesSummary.totalSize)}`}
                    />
                    {uploadFilesSummary.formatText && (
                      <Chip size="small" variant="outlined" label={uploadFilesSummary.formatText} />
                    )}
                    {selectedComplex && (
                      <Chip size="small" variant="outlined" label={selectedComplex.name || 'Комплекс'} />
                    )}
                    {selectedBuilding && (
                      <Chip size="small" variant="outlined" label={selectedBuilding.name || selectedBuilding.address || 'Дом'} />
                    )}
                  </Stack>
                </Paper>

                {uploadResult && (
                  <Box>
                    <Alert severity={uploadResult.errors?.length ? 'warning' : 'success'}>
                      Загружено строк: {uploadResult.total_rows}. Успешно: {uploadResult.imported_rows}. Ошибок:{' '}
                      {uploadResult.errors?.length || 0}.
                      {uploadResult.detected_format
                        ? ` Формат: ${String(uploadResult.detected_format).toUpperCase()}.`
                        : ''}
                      {uploadResult.import_mode === 'history' ? ' Режим: история.' : ' Режим: текущие.'}
                      {!!importIssues.length && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setIssuesDialogOpen(true)}
                          sx={{ ml: { xs: 0, sm: 1.25 }, mt: { xs: 1, sm: 0 } }}
                        >
                          Разобрать ошибки
                        </Button>
                      )}
                    </Alert>

                    {!!uploadResult.warnings?.length && (
                      <Alert severity="info" sx={{ mt: 1.25 }}>
                        <Stack spacing={0.5}>
                          {uploadResult.warnings.map((warning) => (
                            <Typography key={warning} variant="body2">
                              {warning}
                            </Typography>
                          ))}
                        </Stack>
                      </Alert>
                    )}

                    {!!uploadItems.length && (
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 1.25,
                          p: 1.25,
                          borderRadius: 2.5,
                          background: 'rgba(248, 250, 252, 0.72)',
                        }}
                      >
                        <Stack spacing={1}>
                          {uploadItems.map((item) => (
                            <Stack
                              key={item.fileName}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                border: '1px solid rgba(148, 163, 184, 0.16)',
                                background: '#fff',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.fileName}
                              </Typography>
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Chip
                                  size="small"
                                  color={item.status === 'success' ? 'success' : 'error'}
                                  variant="outlined"
                                  label={item.status === 'success' ? 'Загружен' : 'Ошибка'}
                                />
                                {item.status === 'success' && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Импортировано: ${item.result?.imported_rows || 0}`}
                                  />
                                )}
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                )}
              </Stack>
            </StageCard>
          </Stack>

          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 2,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(249,250,251,0.98))',
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Форматы
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <DataObjectRoundedIcon sx={{ fontSize: 18, color: '#0284C7', mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>XML / ISTA</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <GridViewRoundedIcon sx={{ fontSize: 18, color: '#16A34A', mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>XLSX</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TableRowsRoundedIcon sx={{ fontSize: 18, color: '#64748B', mt: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>CSV</strong>
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 3,
                p: 2,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(249,250,251,0.98))',
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Последняя загрузка
                </Typography>
                {!uploadResult ? (
                  <Typography variant="body2" color="text.secondary">Нет данных</Typography>
                ) : (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`Успешно: ${uploadResult.imported_rows}`}
                      />
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`Ошибок: ${warningCount}`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        icon={<TimelineRoundedIcon sx={{ fontSize: 14 }} />}
                        label={uploadResult.import_mode === 'history' ? 'История' : 'Текущие'}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Batch ID: {uploadResult.batch_id || '—'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}>
            <Stack spacing={0.75}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                История импортов
              </Typography>
            </Stack>
          </Box>
          <Divider />
          <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <TextField
                label="Поиск по файлу"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                size="small"
              />
              <TextField
                label="Сортировка"
                select
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event.target.value);
                  setPage(1);
                }}
                size="small"
                sx={{ ...SELECT_FIELD_SX, minWidth: 200 }}
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Порядок"
                select
                value={sortDir}
                onChange={(event) => {
                  setSortDir(event.target.value);
                  setPage(1);
                }}
                size="small"
                sx={{ ...SELECT_FIELD_SX, minWidth: 190 }}
                SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
              >
                {SORT_DIR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Дата с"
                type="date"
                inputProps={{ min: tenantDataStartDate || undefined }}
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event.target.value);
                  setPage(1);
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={DATE_INPUT_STYLE}
              />
              <TextField
                label="Дата по"
                type="date"
                inputProps={{ min: tenantDataStartDate || undefined }}
                value={toDate}
                onChange={(event) => {
                  setToDate(event.target.value);
                  setPage(1);
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={DATE_INPUT_STYLE}
              />
            </Stack>
          </Box>
        </Paper>

        {loading && (
          <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <CircularProgress size={20} />
              <Typography variant="body2">Загрузка истории импортов...</Typography>
            </Stack>
          </Paper>
        )}

        {!loading && batches.length === 0 && (
          <Paper variant="outlined" sx={{ borderRadius: 3, px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              История импортов пуста.
            </Typography>
          </Paper>
        )}

        {!loading && batches.length > 0 && (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              width: '100%',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 10,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(148, 163, 184, 0.45)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(241, 245, 249, 0.7)',
              },
              '& .MuiTableCell-head': {
                backgroundColor: 'rgba(248, 250, 252, 0.96)',
                fontWeight: 700,
                color: 'text.secondary',
                borderBottomColor: 'rgba(148, 163, 184, 0.18)',
              },
              '& .MuiTableCell-body': {
                borderBottomColor: 'rgba(148, 163, 184, 0.14)',
                verticalAlign: 'top',
                py: 0.75,
                px: 1,
              },
              '& .MuiTableCell-head, & .MuiTableCell-body': {
                whiteSpace: 'nowrap',
              },
            }}
          >
            <Table size="small" sx={{ minWidth: showActionsColumn ? 920 : 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell>Импорт</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Автор</TableCell>
                  <TableCell>Формат</TableCell>
                  <TableCell align="right">Строк</TableCell>
                  <TableCell align="right">Успешно</TableCell>
                  <TableCell align="right">Пропущено</TableCell>
                  <TableCell>Статус</TableCell>
                  {showActionsColumn && <TableCell align="right">Действия</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedBatches.map((group) => {
                  const isExpanded = Boolean(expandedGroups[group.key]);
                  const firstBatch = group.items[0];
                  const firstKind = detectFileKind(firstBatch?.file_name);
                  const firstRolledBack = Boolean(firstBatch?.rolled_back_at);
                  const firstRollingBack = rollingBackId === firstBatch?.id;

                  return (
                    <React.Fragment key={group.key}>
                      <TableRow
                        hover
                        sx={{
                          position: 'relative',
                          backgroundColor: group.hasXml ? 'rgba(240, 249, 255, 0.45)' : '#fff',
                          transition: 'transform 180ms ease, box-shadow 180ms ease',
                          '&:hover': {
                            transform: showActionsColumn ? 'none' : 'translateX(-6px)',
                            boxShadow: showActionsColumn ? 'none' : '0 14px 28px rgba(15,23,42,0.05)',
                            zIndex: 2,
                          },
                          '&:hover .import-row-actions': {
                            opacity: showActionsColumn ? 0 : 1,
                            pointerEvents: showActionsColumn ? 'none' : 'auto',
                            width: showActionsColumn ? 0 : 54,
                            minWidth: showActionsColumn ? 0 : 54,
                            transform: showActionsColumn ? 'translateX(8px)' : 'translateX(0)',
                          },
                        }}
                      >
                        <TableCell>
                          {group.isMulti ? (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedGroups((current) => ({
                                  ...current,
                                  [group.key]: !current[group.key],
                                }))
                              }
                            >
                              {isExpanded ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: showActionsColumn ? 180 : 230 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                          <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {group.isMulti
                                ? `Пакетная загрузка (${group.items.length})`
                                : firstBatch?.file_name || 'Импорт'}
                            </Typography>
                            {group.isMulti && (
                              <Typography variant="caption" color="text.secondary">
                                {group.items
                                  .map((item) => item.file_name)
                                  .filter(Boolean)
                                  .slice(0, 2)
                                  .join(', ')}
                                {group.items.length > 2 ? ` и еще ${group.items.length - 2}` : ''}
                              </Typography>
                            )}
                          </Stack>
                          {!showActionsColumn && !group.isMulti && !firstRolledBack && canRollback && (
                            <Box
                              className="import-row-actions"
                              sx={{
                                width: 0,
                                minWidth: 0,
                                overflow: 'hidden',
                                opacity: 0,
                                pointerEvents: 'none',
                                transform: 'translateX(10px)',
                                transition:
                                  'width 180ms ease, min-width 180ms ease, transform 180ms ease, opacity 180ms ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <IconButton
                                aria-label="Откатить импорт"
                                size="small"
                                sx={HOVER_ACTION_BUTTON_SX}
                                onClick={() => requestRollback(firstBatch)}
                                disabled={firstRollingBack}
                              >
                                {firstRollingBack ? <CircularProgress size={16} /> : <UndoRoundedIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Box>
                          )}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDate(group.createdAt)}</TableCell>
                        <TableCell>{group.author || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {group.isMulti ? (
                              <Chip size="small" variant="outlined" label="Группа" />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={firstKind.color}
                                icon={firstKind.icon}
                                label={firstKind.label}
                              />
                            )}
                            {group.hasXml && <Chip size="small" variant="outlined" color="info" label="ISTA / XML" />}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{group.totalRows || '—'}</TableCell>
                        <TableCell align="right" sx={{ color: '#15803D', fontWeight: 700 }}>
                          {group.importedRows || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#B45309', fontWeight: 700 }}>
                          {group.skippedRows || '—'}
                        </TableCell>
                        <TableCell>
                          {group.rolledBack ? (
                            <Chip size="small" color="warning" variant="outlined" label="Откат выполнен" />
                          ) : (
                            <Chip size="small" color="success" variant="outlined" label="Активен" />
                          )}
                        </TableCell>
                        {showActionsColumn && (
                          <TableCell align="right">
                            {!group.isMulti && !firstRolledBack && canRollback ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={firstRollingBack ? <CircularProgress size={14} /> : <UndoRoundedIcon />}
                                onClick={() => requestRollback(firstBatch)}
                                disabled={firstRollingBack}
                              >
                                Откатить
                              </Button>
                            ) : group.isMulti ? (
                              <Typography variant="caption" color="text.secondary">
                                {isExpanded ? 'Свернуть' : 'Раскрыть'}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        )}
                      </TableRow>

                      {group.isMulti && (
                        <TableRow>
                          <TableCell colSpan={showActionsColumn ? 10 : 9} sx={{ p: 0, borderBottom: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 1.5, backgroundColor: 'rgba(248, 250, 252, 0.72)' }}>
                                <Table size="small" sx={{ backgroundColor: '#fff' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Файл</TableCell>
                                      <TableCell>Дата</TableCell>
                                      <TableCell>Формат</TableCell>
                                      <TableCell align="right">Строк</TableCell>
                                      <TableCell align="right">Успешно</TableCell>
                                      <TableCell align="right">Пропущено</TableCell>
                                      <TableCell>Статус</TableCell>
                                      {showActionsColumn && <TableCell align="right">Действия</TableCell>}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.items.map((batch) => {
                                      const rolledBack = Boolean(batch.rolled_back_at);
                                      const isRollingBack = rollingBackId === batch.id;
                                      const fileKind = detectFileKind(batch.file_name);

                                      return (
                                        <TableRow
                                          key={batch.id}
                                          hover
                                          sx={{
                                            position: 'relative',
                                            transition: 'transform 180ms ease, box-shadow 180ms ease',
                                            '&:hover': {
                                              transform: showActionsColumn ? 'none' : 'translateX(-4px)',
                                              boxShadow: showActionsColumn ? 'none' : '0 10px 20px rgba(15,23,42,0.04)',
                                              zIndex: 2,
                                            },
                                            '&:hover .import-child-actions': {
                                              opacity: showActionsColumn ? 0 : 1,
                                              pointerEvents: showActionsColumn ? 'none' : 'auto',
                                              width: showActionsColumn ? 0 : 54,
                                              minWidth: showActionsColumn ? 0 : 54,
                                              transform: showActionsColumn ? 'translateX(8px)' : 'translateX(0)',
                                            },
                                          }}
                                        >
                                          <TableCell sx={{ minWidth: showActionsColumn ? 180 : 230 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, flex: 1 }}>
                                                {batch.file_name || 'Импорт'}
                                              </Typography>
                                              {!showActionsColumn && !rolledBack && canRollback && (
                                                <Box
                                                  className="import-child-actions"
                                                  sx={{
                                                    width: 0,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    opacity: 0,
                                                    pointerEvents: 'none',
                                                    transform: 'translateX(10px)',
                                                    transition:
                                                      'width 180ms ease, min-width 180ms ease, transform 180ms ease, opacity 180ms ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                  }}
                                                >
                                                  <IconButton
                                                    aria-label="Откатить импорт"
                                                    size="small"
                                                    sx={HOVER_ACTION_BUTTON_SX}
                                                    onClick={() => requestRollback(batch)}
                                                    disabled={isRollingBack}
                                                  >
                                                    {isRollingBack ? (
                                                      <CircularProgress size={16} />
                                                    ) : (
                                                      <UndoRoundedIcon sx={{ fontSize: 18 }} />
                                                    )}
                                                  </IconButton>
                                                </Box>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>{formatDate(batch.created_at)}</TableCell>
                                          <TableCell>
                                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                              <Chip size="small" variant="outlined" color={fileKind.color} label={fileKind.label} />
                                              {fileKind.isIsta && <Chip size="small" variant="outlined" color="info" label="ISTA" />}
                                            </Stack>
                                          </TableCell>
                                          <TableCell align="right">{batch.total_rows ?? '—'}</TableCell>
                                          <TableCell align="right">{batch.imported_rows ?? '—'}</TableCell>
                                          <TableCell align="right">{batch.skipped_rows ?? '—'}</TableCell>
                                          <TableCell>
                                            {rolledBack ? (
                                              <Chip size="small" color="warning" variant="outlined" label="Откат выполнен" />
                                            ) : (
                                              <Chip size="small" color="success" variant="outlined" label="Активен" />
                                            )}
                                          </TableCell>
                                          {showActionsColumn && (
                                            <TableCell align="right">
                                              {!rolledBack && canRollback ? (
                                                <Button
                                                  variant="outlined"
                                                  size="small"
                                                  startIcon={isRollingBack ? <CircularProgress size={14} /> : <UndoRoundedIcon />}
                                                  onClick={() => requestRollback(batch)}
                                                  disabled={isRollingBack}
                                                >
                                                  Откатить
                                                </Button>
                                              ) : (
                                                '—'
                                              )}
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {false && !loading &&
          groupedBatches
            .filter((group) => group.isMulti)
            .map((group) => {
              const isExpanded = Boolean(expandedGroups[group.key]);

              return (
                <Paper
                  key={group.key}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    borderColor: group.hasXml ? 'rgba(2, 132, 199, 0.24)' : 'rgba(148, 163, 184, 0.22)',
                    background: group.hasXml
                      ? 'linear-gradient(180deg, rgba(240,249,255,0.78) 0%, rgba(255,255,255,0.96) 34%, rgba(255,255,255,1) 100%)'
                      : 'rgba(255,255,255,0.98)',
                  }}
                >
                  <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {`Пакетная загрузка · ${group.items.length} файлов`}
                          </Typography>
                          <Chip size="small" variant="outlined" label="Группа" />
                          {group.hasXml && <Chip size="small" variant="outlined" color="info" label="ISTA / XML" />}
                          {group.rolledBack && <Chip size="small" color="warning" variant="outlined" label="Откат выполнен" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {`Дата: ${formatDate(group.createdAt)}`}
                          {group.author ? ` · Автор: ${group.author}` : ''}
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpandedGroups((current) => ({
                            ...current,
                            [group.key]: !current[group.key],
                          }))
                        }
                      >
                        {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                      </IconButton>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" variant="outlined" label={`Файлов: ${group.items.length}`} />
                      <Chip size="small" variant="outlined" label={`Строк: ${group.totalRows || '—'}`} />
                      <Chip size="small" variant="outlined" color="success" label={`Успешно: ${group.importedRows || '—'}`} />
                      <Chip size="small" variant="outlined" color="warning" label={`Пропущено: ${group.skippedRows || '—'}`} />
                    </Stack>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider />
                    <Stack spacing={1.25} sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
                      {group.items.map((batch) => {
                        const rolledBack = Boolean(batch.rolled_back_at);
                        const rolledBy = batch.rolled_back_by_email || batch.rolled_back_by_user_id;
                        const isRollingBack = rollingBackId === batch.id;
                        const fileKind = detectFileKind(batch.file_name);
                        const isXmlIsta = fileKind.isIsta;

                        return (
                          <Paper
                            key={batch.id}
                            variant="outlined"
                            sx={{
                              borderRadius: 2.5,
                              px: 2,
                              py: 1.5,
                              background: '#fff',
                              borderColor: isXmlIsta ? 'rgba(2, 132, 199, 0.18)' : 'rgba(148, 163, 184, 0.16)',
                            }}
                          >
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                    {batch.file_name || 'Импорт'}
                                  </Typography>
                                  <Chip size="small" variant="outlined" color={fileKind.color} label={fileKind.label} />
                                  {isXmlIsta && <Chip size="small" variant="outlined" color="info" label="ISTA" />}
                                  {rolledBack && <Chip size="small" color="warning" variant="outlined" label="Откат выполнен" />}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                  {`Дата: ${formatDate(batch.created_at)}`}
                                  {rolledBack && rolledBy ? ` · Откатил: ${rolledBy}` : ''}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                  <Chip size="small" variant="outlined" label={`Строк: ${batch.total_rows ?? '—'}`} />
                                  <Chip size="small" variant="outlined" color="success" label={`Успешно: ${batch.imported_rows ?? '—'}`} />
                                  <Chip size="small" variant="outlined" color="warning" label={`Пропущено: ${batch.skipped_rows ?? '—'}`} />
                                </Stack>
                              </Box>

                              {!rolledBack && canRollback && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={isRollingBack ? <CircularProgress size={14} /> : <UndoRoundedIcon />}
                                  onClick={() => requestRollback(batch)}
                                  disabled={isRollingBack}
                                >
                                  Откатить
                                </Button>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Collapse>
                </Paper>
              );
            })}

        {false && !loading &&
          groupedBatches
            .filter((group) => !group.isMulti)
            .map((group) => group.items[0])
            .map((batch) => {
            const rolledBack = Boolean(batch.rolled_back_at);
            const author = batch.created_by_email || batch.created_by_user_id;
            const rolledBy = batch.rolled_back_by_email || batch.rolled_back_by_user_id;
            const isRollingBack = rollingBackId === batch.id;
            const fileKind = detectFileKind(batch.file_name);
            const isXmlIsta = fileKind.isIsta;

            return (
              <Paper
                key={batch.id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  borderColor: isXmlIsta ? 'rgba(2, 132, 199, 0.24)' : undefined,
                  background: isXmlIsta
                    ? 'linear-gradient(180deg, rgba(240,249,255,0.78) 0%, rgba(255,255,255,0.96) 34%, rgba(255,255,255,1) 100%)'
                    : undefined,
                  '&::before': isXmlIsta
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background:
                          'linear-gradient(180deg, rgba(14,165,233,0.92) 0%, rgba(2,132,199,0.88) 100%)',
                      }
                    : undefined,
                }}
              >
                <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {batch.file_name || 'Импорт'}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={fileKind.color}
                          icon={fileKind.icon}
                          label={fileKind.label}
                        />
                        {isXmlIsta && <Chip size="small" variant="outlined" color="info" label="ISTA" />}
                        {rolledBack && (
                          <Chip size="small" color="warning" variant="outlined" label="Откат выполнен" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Дата: {formatDate(batch.created_at)}
                        {author ? ` · Автор: ${author}` : ''}
                        {rolledBack && rolledBy ? ` · Откатил: ${rolledBy}` : ''}
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      {!rolledBack && canRollback && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isRollingBack ? <CircularProgress size={14} /> : <UndoRoundedIcon />}
                          onClick={() => requestRollback(batch)}
                          disabled={isRollingBack}
                        >
                          Откатить
                        </Button>
                      )}
                      {!rolledBack && !canRollback && (
                        <Chip size="small" variant="outlined" label="Нет прав на откат" />
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Divider />

                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={`Строк: ${batch.total_rows ?? '—'}`} />
                    <Chip size="small" variant="outlined" color="success" label={`Успешно: ${batch.imported_rows ?? '—'}`} />
                    <Chip size="small" variant="outlined" color="warning" label={`Пропущено: ${batch.skipped_rows ?? '—'}`} />
                    {isXmlIsta && <Chip size="small" variant="outlined" color="info" label="ISTA XML" />}
                  </Stack>
                </Box>
              </Paper>
            );
          })}

        {!loading && pageCount > 1 && (
          <Stack direction="row" justifyContent="center" sx={{ pt: 0.5 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, nextPage) => setPage(nextPage)}
              color="primary"
            />
          </Stack>
        )}
      </Stack>

      <Dialog
        open={issuesDialogOpen}
        onClose={() => setIssuesDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Проблемные строки импорта
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="info">
              Здесь показаны показания, которые не были добавлены. Если устройство не найдено, проверьте карточку квартиры:
              возможно, прибор нужно добавить или оформить замену.
            </Alert>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: '62vh',
                borderRadius: 2.5,
                '& .MuiTableCell-root': { py: 1, fontSize: 13 },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: '#F8FAFC',
                  fontWeight: 800,
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Файл</TableCell>
                    <TableCell>Строка</TableCell>
                    <TableCell>Устройство</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Показание</TableCell>
                    <TableCell>Причина</TableCell>
                    <TableCell>Что сделать</TableCell>
                    <TableCell align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importIssues.map((item, index) => {
                    const deviceId = item.serial_number || item.dev_eui || '';
                    const actionText = getImportIssueAction(item);
                    return (
                      <TableRow key={`${item.file_name || 'file'}-${item.row_index}-${index}`} hover>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="body2" noWrap title={item.file_name || ''}>
                            {item.file_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.row_index}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {deviceId || '—'}
                            </Typography>
                            {item.dev_eui && item.serial_number && (
                              <Typography variant="caption" color="text.secondary">
                                DevEUI: {item.dev_eui}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{item.reading_date || '—'}</TableCell>
                        <TableCell>{item.reading_value || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2">{item.message || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" color="warning" label={actionText} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                            {!!deviceId && (
                              <Tooltip title="Скопировать номер">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (navigator?.clipboard?.writeText) {
                                      void navigator.clipboard.writeText(deviceId);
                                    }
                                  }}
                                >
                                  <ContentCopyRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!selectedBuildingId}
                              onClick={() => {
                                setIssuesDialogOpen(false);
                                navigate(`/building/${selectedBuildingId}/corps`);
                              }}
                            >
                              Устройства
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuesDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Откатить импорт?"
        description={`Откатить импорт ${confirmBatch?.file_name ? `«${confirmBatch.file_name}»` : ''}? Все показания из этой загрузки будут удалены.`}
        confirmText="Откатить"
        confirmColor="error"
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmBatch(null);
        }}
        onConfirm={handleRollback}
      />
    </Box>
  );
}
